import { useCallback, useEffect, useMemo, useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import { ContactList } from '../components/ContactList';
import { ContactForm } from '../components/ContactForm';
import { CategoryModal } from '../components/CategoryModal';
import { CSVImportModal } from '../components/CSVImportModal';
import { CertificationImportModal } from '../components/CertificationImportModal';
import { SearchAndFilters } from '../components/SearchAndFilters';
import { Pagination } from '../components/Pagination';
import { Category, Contact } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export function ContactsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [updatedFilter, setUpdatedFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [isCertificationImportModalOpen, setIsCertificationImportModalOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [isApplyingCategory, setIsApplyingCategory] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  const { contacts, total, totalPages, loading, error, refresh, deleteContact } = useContacts({
    search: search || undefined,
    page: currentPage,
    pageSize,
  });

  const filteredContacts = useMemo(() => {
    const now = new Date();

    return contacts.filter((contact) => {
      const categoryMatch = !categoryFilter || contact.categoriaId === categoryFilter;

      const isComplete = Boolean(contact.categoriaId && (contact.processo || contact.mandado));
      const statusMatch = !statusFilter || (statusFilter === 'completo' ? isComplete : !isComplete);

      let updatedMatch = true;
      if (updatedFilter) {
        const updatedAt = new Date(contact.atualizadoEm);
        const diffMs = now.getTime() - updatedAt.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);

        if (updatedFilter === 'today') {
          updatedMatch = updatedAt.toDateString() === now.toDateString();
        } else if (updatedFilter === '7d') {
          updatedMatch = diffDays <= 7;
        } else if (updatedFilter === '30d') {
          updatedMatch = diffDays <= 30;
        }
      }

      return categoryMatch && statusMatch && updatedMatch;
    });
  }, [contacts, categoryFilter, statusFilter, updatedFilter]);

  const pageRangeStart = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const pageRangeEnd = Math.min(currentPage * pageSize, total);

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setUpdatedFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setIsFormOpen(true);
  };

  const handleFormSuccess = async () => {
    await refresh();
    setIsFormOpen(false);
    setEditingContact(undefined);
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
  };

  const handleNewContact = () => {
    setEditingContact(undefined);
    setIsFormOpen(true);
  };

  const loadCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const data = await apiService.getAllCategories();
      setCategories(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar categorias';
      toast.error(errorMessage);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isCategoryModalOpen) {
      loadCategories();
    }
  }, [isCategoryModalOpen, loadCategories]);

  useEffect(() => {
    setSelectedContacts((prev) => prev.filter((id) => filteredContacts.some((contact) => contact.id === id)));
  }, [filteredContacts]);

  const handleApplyCategory = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um destinatário');
      return;
    }

    if (!bulkCategoryId) {
      toast.error('Selecione uma categoria para aplicar');
      return;
    }

    try {
      setIsApplyingCategory(true);
      const categoriaId = bulkCategoryId === '__clear__' ? null : bulkCategoryId;
      await apiService.updateContactsCategory(selectedContacts, categoriaId);
      toast.success('Categoria atualizada para os destinatários selecionados');
      setSelectedContacts([]);
      refresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao atualizar a categoria';
      toast.error(errorMessage);
    } finally {
      setIsApplyingCategory(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um destinatário');
      return;
    }

    const confirmed = window.confirm(
      `Tem certeza que deseja excluir ${selectedContacts.length} destinatário(s)? Esta ação não pode ser desfeita.`
    );
    if (!confirmed) {
      return;
    }

    try {
      setIsDeletingSelected(true);
      const deletions = selectedContacts.map((id) => apiService.deleteContact(id).then(() => id));
      const results = await Promise.allSettled(deletions);
      const failedIds: string[] = [];

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          failedIds.push(selectedContacts[index]);
        }
      });

      const successCount = selectedContacts.length - failedIds.length;

      if (successCount > 0) {
        toast.success(`${successCount} destinatário(s) excluído(s) com sucesso`);
      }
      if (failedIds.length > 0) {
        toast.error(`Falha ao excluir ${failedIds.length} destinatário(s). Tente novamente.`);
      }

      setSelectedContacts(failedIds);
      refresh();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao excluir destinatários';
      toast.error(errorMessage);
    } finally {
      setIsDeletingSelected(false);
    }
  };

  const handleToggleContactSelection = (id: string) => {
    setSelectedContacts((prev) => (prev.includes(id) ? prev.filter((contactId) => contactId !== id) : [...prev, id]));
  };

  const handleToggleAllContacts = () => {
    if (filteredContacts.length === 0) {
      return;
    }

    const allVisibleSelected = filteredContacts.every((contact) => selectedContacts.includes(contact.id));

    if (allVisibleSelected) {
      setSelectedContacts([]);
      return;
    }

    setSelectedContacts(filteredContacts.map((contact) => contact.id));
  };

  const handleBulkSendMessage = () => {
    if (selectedContacts.length === 0) {
      toast.error('Selecione pelo menos um destinatário');
      return;
    }

    toast.success(`${selectedContacts.length} destinatário(s) selecionado(s) para envio.`);
    navigate('/campanhas');
  };

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-red-600">Erro</h1>
          <p className="mb-4 text-gray-600">{error}</p>
          <button
            onClick={refresh}
            className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`space-y-6 ${embedded ? 'p-0' : 'p-4 sm:p-6'}`}>
        {embedded ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Destinatários</p>
                <p className="mt-1 text-sm text-slate-500">
                  Gerencie sua base de destinatários antes de seguir para os envios.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleNewContact}
                  className="rounded-xl bg-gradient-to-r from-sky-700 via-blue-700 to-indigo-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:from-sky-800 hover:via-blue-800 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-blue-100"
                  aria-label="Criar novo destinatário"
                >
                  + Novo destinatário
                </button>

                <button
                  onClick={() => setIsCSVImportModalOpen(true)}
                  className="rounded-xl border border-slate-200 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-100"
                  aria-label="Importar destinatários via CSV ou Excel"
                >
                  Importar para Envio
                </button>
              </div>
            </div>
          </div>
        ) : (
        <header className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Destinatários</h1>
              <p className="mt-1 text-sm text-slate-500 sm:text-base">
                Gerencie e organize seus destinatários para envio de mandados
              </p>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={handleNewContact}
                className="rounded-xl bg-gradient-to-r from-sky-700 via-blue-700 to-indigo-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:from-sky-800 hover:via-blue-800 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-blue-100"
                aria-label="Criar novo destinatário"
              >
                + Novo destinatário
              </button>

              <button
                onClick={() => setIsCSVImportModalOpen(true)}
                className="rounded-xl border border-slate-200 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-4 focus:ring-slate-100"
                aria-label="Importar destinatários via CSV ou Excel"
              >
                Importar para Envio
              </button>

              <button
                onClick={() => setIsCertificationImportModalOpen(true)}
                className="rounded-xl border border-emerald-200 bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                aria-label="Importar para certificacao"
              >
                Importar para Certificação
              </button>

              <button
                onClick={() => setIsCategoryModalOpen(true)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-100"
                aria-label="Gerenciar categorias"
              >
                Categorias
              </button>
            </div>
          </div>
        </header>
        )}

        <SearchAndFilters
          search={search}
          categoryFilter={categoryFilter}
          statusFilter={statusFilter}
          updatedFilter={updatedFilter}
          categoryOptions={categories}
          onSearchChange={handleSearchChange}
          onCategoryFilterChange={setCategoryFilter}
          onStatusFilterChange={setStatusFilter}
          onUpdatedFilterChange={setUpdatedFilter}
          onClearFilters={handleClearFilters}
        />

        {selectedContacts.length > 0 && (
          <div className="sticky top-[calc(var(--top-nav-height)+12px)] z-20 rounded-xl border border-blue-200 bg-blue-50/95 p-3 shadow-sm backdrop-blur sm:p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm font-semibold text-blue-900">
                {selectedContacts.length} selecionado(s)
              </p>

              <div className="flex flex-wrap gap-2">
                <select
                  value={bulkCategoryId}
                  onChange={(e) => setBulkCategoryId(e.target.value)}
                  className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-blue-200"
                  disabled={categoriesLoading}
                >
                  <option value="">Categoria</option>
                  <option value="__clear__">Remover categoria</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nome}
                    </option>
                  ))}
                </select>

                <button
                  onClick={handleApplyCategory}
                  disabled={isApplyingCategory || isDeletingSelected || !bulkCategoryId}
                  className="rounded-lg bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isApplyingCategory ? 'Aplicando...' : 'Aplicar categoria'}
                </button>

                <button
                  onClick={handleBulkSendMessage}
                  disabled={isDeletingSelected}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Enviar mensagem
                </button>

                <button
                  onClick={handleBulkDelete}
                  disabled={isDeletingSelected}
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isDeletingSelected ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Lista de destinatários</p>
              <p className="text-xs text-slate-500">
                {filteredContacts.length} item(ns) nesta página após filtros
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-600">
              <span>Itens por página</span>
              <select
                value={pageSize}
                onChange={(event) => handlePageSizeChange(Number(event.target.value))}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-200"
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <ContactList
            contacts={filteredContacts}
            loading={loading}
            onEdit={handleEdit}
            onDelete={deleteContact}
            selectedContactIds={selectedContacts}
            onToggleContactSelection={handleToggleContactSelection}
            onToggleAllContacts={handleToggleAllContacts}
          />
        </section>

        <footer className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-600">
            Exibindo {pageRangeStart}-{pageRangeEnd} de {total}
          </p>

          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </footer>
      </div>

      {isFormOpen && <ContactForm contact={editingContact} onSuccess={handleFormSuccess} onCancel={handleFormCancel} />}

      <CategoryModal isOpen={isCategoryModalOpen} onClose={() => setIsCategoryModalOpen(false)} />

      <CSVImportModal
        isOpen={isCSVImportModalOpen}
        onClose={() => setIsCSVImportModalOpen(false)}
        onSuccess={() => {
          refresh();
          setIsCSVImportModalOpen(false);
        }}
      />

      <CertificationImportModal
        isOpen={isCertificationImportModalOpen}
        onClose={() => setIsCertificationImportModalOpen(false)}
        onSuccess={() => {
          setIsCertificationImportModalOpen(false);
          sessionStorage.setItem('certification_import_refresh', '1');
          navigate('/certificacao', { state: { forceRefreshCertification: true } });
        }}
      />
    </>
  );
}
